import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionableNotification,
  Button,
  Checkbox,
  InlineNotification,
  Link as CarbonLink,
  Loading,
  Tag,
} from '@carbon/react';
import { Information } from '@carbon/react/icons';
import type { Row } from '@tanstack/react-table';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { PageHeader } from 'components/page-header';
import { FloatingTooltip } from 'components/floating-tooltip';
import { format0 } from 'utils/number';
import { entityIdSuffix } from 'utils/entity-id';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import request from 'lib/request';
import type { BulkType, Run, Subcluster, SubclusterMember } from '../types';
import {
  AddressList,
  executionColumns,
  MatchTypeTag,
  PLAIN_TABLE_CONFIG,
  ProcessProgress,
  ReverseConfirmModal,
  StatusTag,
} from '../shared';
import { subclusterStatusLabel } from '../status-vocabulary';
import { AddressRunPage } from './address-run-page';
import {
  errorFrom,
  useClusteringStatus,
  useRunBulkType,
  useRunExecutions,
  useRunFailures,
  useRunProcesses,
  useStatusToggle,
} from './hooks';
import styles from '../page.module.scss';

type SortState = { id: string; desc: boolean } | null;

const DEFAULT_PAGE_SIZE = 50;

const failureColumns: DataTableColumnDef<Subcluster>[] = [
  {
    accessorKey: 'normalizedName',
    header: 'Normalized name',
    size: 240,
    enableSorting: false,
    cell: ({ getValue }) => <strong>{getValue() as string}</strong>,
  },
  {
    id: 'members',
    accessorFn: (row) => row.members.length,
    header: 'Members',
    size: 90,
    enableSorting: false,
    cell: ({ getValue }) => format0(getValue() as number),
  },
  {
    accessorKey: 'exclusionReason',
    header: 'Reason',
    size: 620,
    enableSorting: false,
    meta: { wrap: true, align: 'left' },
    cell: ({ getValue }) => (getValue() as string | null) ?? '--',
  },
];

// Review & Execute for one entity clustering run.
const EntityRunPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const runId = Number(id);

  const [subclusters, setSubclusters] = useState<Subcluster[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [matchTypeCounts, setMatchTypeCounts] = useState<
    Record<string, number>
  >({});
  const [subsLoading, setSubsLoading] = useState(true);
  const pageRef = useRef<{ page: number; pageSize: number; sort: SortState }>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [runToReverse, setRunToReverse] = useState<Run | null>(null);
  const { failures, failuresTotal, loadFailures, setFailures } =
    useRunFailures<Subcluster>(runId, setError);

  const loadSubclusters = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE, sort: SortState = null) => {
      pageRef.current = { page, pageSize, sort };
      setSubsLoading(true);
      const offset = (page - 1) * pageSize;
      const sortQuery = sort
        ? `&sort=${sort.id}&direction=${sort.desc ? 'desc' : 'asc'}`
        : '';
      const json = (await request.get(
        `/admin/bulk_dedupe/subclusters?clusteringProcessId=${runId}&limit=${pageSize}&offset=${offset}${sortQuery}`
      )) as {
        subclusters?: Subcluster[];
        total?: number;
        counts?: Record<string, number>;
        matchTypeCounts?: Record<string, number>;
      };
      const err = errorFrom(json);
      if (err) {
        setError(err);
        setSubsLoading(false);
        return;
      }
      setSubclusters(json?.subclusters ?? []);
      setTotal(json?.total ?? 0);
      setCounts(json?.counts ?? {});
      setMatchTypeCounts(json?.matchTypeCounts ?? {});
      setSubsLoading(false);
    },
    [runId]
  );

  const reloadPage = useCallback(() => {
    const { page, pageSize, sort } = pageRef.current;
    loadSubclusters(page, pageSize, sort);
  }, [loadSubclusters]);

  const { executions, loadExecutions } = useRunExecutions(runId, setError);

  useEffect(() => {
    loadSubclusters();
    loadFailures();
  }, [loadSubclusters, loadFailures]);

  const { clusterStatus, clustering } = useClusteringStatus(
    runId,
    loadSubclusters,
    setError
  );

  const refreshAll = useCallback(() => {
    reloadPage();
    loadFailures();
    loadExecutions();
  }, [reloadPage, loadFailures, loadExecutions]);

  const {
    mergeProc,
    mergeStatus,
    reverseProc,
    reverseStatus,
    mergeRunning,
    reversalRunning,
    executeBusy,
    execute,
    reverse,
  } = useRunProcesses({
    runId,
    bulkType: 'entities',
    setError,
    onMergeDone: refreshAll,
    onReverseDone: refreshAll,
  });

  // Refresh the failures panel as the merge progresses — keyed to step-count
  // changes rather than every 1.5s poll tick, so a long-draining run doesn't
  // hammer the (member-serializing) failures endpoint.
  useEffect(() => {
    if (mergeProc && mergeStatus) loadFailures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergeStatus?.completed_steps, mergeStatus?.failed_steps]);

  const runExecute = () => {
    setFailures([]);
    execute();
  };

  const confirmReverse = () => {
    if (!runToReverse) return;
    reverse('/admin/bulk_dedupe/reverse', { mergeProcessId: runToReverse.id });
    setRunToReverse(null);
  };

  const mergeableCount = counts.in_scope ?? 0;

  // Replace the row from the server's response and refresh counts so the
  // Execute button stays exact, keeping the columns stable (no re-init).
  const { toggle: toggleSubcluster, pendingIds } = useStatusToggle<Subcluster>(
    useCallback(
      (updated, freshCounts, skipped) => {
        const [row] = updated;
        if (row)
          setSubclusters((prev) =>
            prev.map((s) => (s.id === row.id ? row : s))
          );
        setCounts(freshCounts);
        // The server now skips a group no longer under review; show why and
        // reload rather than leaving a stale row with no message.
        if (skipped > 0) {
          setError(
            'This group is no longer under review; the run was reloaded.'
          );
          reloadPage();
        }
      },
      [reloadPage]
    ),
    setError
  );

  // Captured handlers use functional state updates / row data only, so the
  // memoized columns stay correct while keeping the table from re-initializing.
  const columns = useMemo<DataTableColumnDef<Subcluster>[]>(
    () => [
      {
        id: 'included',
        header: '',
        size: 48,
        enableSorting: false,
        cell: ({ row }) => {
          const sc = row.original;
          // Only in-scope/excluded candidates can be toggled; a combined/failed/
          // reversed row is terminal, and none can toggle while a merge runs.
          const toggleable =
            sc.status === 'in_scope' || sc.status === 'excluded';
          // The shared DataTable clips cells (overflow:hidden), which cuts
          // Carbon's checkbox on focus. We can't modify that component, so the
          // wrapper class lets our page SCSS un-clip just this cell via :has().
          return (
            <div className={styles.includeCell}>
              <Checkbox
                id={`include-${sc.id}`}
                labelText={`Include ${sc.normalizedName}`}
                hideLabel
                checked={sc.status === 'in_scope'}
                disabled={
                  !toggleable ||
                  mergeRunning ||
                  clustering ||
                  pendingIds.has(sc.id)
                }
                onChange={(_e, { checked }) =>
                  toggleSubcluster([sc.id], checked)
                }
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 100,
        enableSorting: false,
        cell: ({ getValue }) => (
          <StatusTag status={getValue() as string} bulkType="entities" />
        ),
      },
      {
        accessorKey: 'normalizedName',
        // FloatingTooltip (portaled, width-capped, wrapping) — Carbon's
        // TooltipIcon overflowed its content across the header row here.
        header: () => (
          <span className={styles.labelWithTooltip}>
            Normalized name{' '}
            <FloatingTooltip
              content="How names are matched: a case-folded exact match — punctuation and whitespace are significant. Individuals are composed as “last, first [middle]” and matched in both orderings. Any spelling difference, or a differing/added middle initial, counts as a different name; there is no fuzzy matching."
              placement="top"
            >
              <Information />
            </FloatingTooltip>
          </span>
        ),
        size: 260,
        enableSorting: true,
        cell: ({ getValue }) => <strong>{getValue() as string}</strong>,
      },
      {
        accessorKey: 'matchedAddresses',
        header: 'Matched address(es)',
        size: 300,
        enableSorting: false,
        meta: { wrap: true, align: 'left' },
        cell: ({ getValue }) => <AddressList values={getValue() as string[]} />,
      },
      {
        accessorKey: 'matchType',
        header: 'Linked by',
        size: 150,
        enableSorting: false,
        cell: ({ getValue }) => (
          <MatchTypeTag value={getValue() as Subcluster['matchType']} />
        ),
      },
      {
        id: 'member_count',
        // accessorFn (not just id) is required for TanStack to treat the column
        // as sortable; server-side sort is wired via onSortChange.
        accessorFn: (row) => row.members.length,
        header: 'Members',
        size: 110,
        enableSorting: true,
        cell: ({ getValue }) => format0(getValue() as number),
      },
    ],
    [clustering, mergeRunning, pendingIds, toggleSubcluster]
  );

  const memberColumns = useMemo<DataTableColumnDef<SubclusterMember>[]>(
    () => [
      {
        accessorKey: 'role',
        header: 'Outcome',
        size: 120,
        cell: ({ row }) => {
          const m = row.original;
          // Held back: merging this member would drop a colliding hard-delete
          // row, so it is kept as a separate entity to avoid data loss.
          if (m.collision) {
            return (
              <FloatingTooltip
                content="Would drop a colliding reference row — kept separate to avoid data loss"
                placement="top"
              >
                <Tag type="cyan" size="sm">
                  held back
                </Tag>
              </FloatingTooltip>
            );
          }
          // Sources are merged away; the target is left in place.
          const deduped = m.role === 'source';
          return (
            <Tag type={deduped ? 'gray' : 'green'} size="sm">
              {deduped ? 'deduplicated' : 'survives'}
            </Tag>
          );
        },
      },
      {
        id: 'entity',
        header: 'Entity',
        size: 280,
        cell: ({ row }) => {
          const m = row.original;
          return (
            <span>
              <CarbonLink
                href={`/entities/${m.entityId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {m.name ?? `Entity ${m.entityId}`}
              </CarbonLink>{' '}
              <span className={styles.addressLine}>
                {entityIdSuffix(m.entityId)} · {m.entityType}
              </span>
            </span>
          );
        },
      },
      {
        id: 'link',
        header: 'Linked by',
        size: 450,
        meta: { wrap: true, align: 'left' },
        cell: ({ row }) => {
          const m = row.original;
          const hasAddresses = m.matchedAddresses?.length > 0;
          if (!hasAddresses && !m.aliasConfirmed) return <>--</>;
          return (
            <>
              {hasAddresses && <AddressList values={m.matchedAddresses} />}
              {m.aliasConfirmed && (
                <Tag type="teal" size="sm">
                  Alias-confirmed
                </Tag>
              )}
            </>
          );
        },
      },
    ],
    []
  );

  const renderMembers = useCallback(
    (row: Row<Subcluster>) => {
      const sc = row.original;
      return (
        <div className={styles.recordsSubTable}>
          {sc.exclusionReason && (
            <p className={styles.addressLine}>Reason: {sc.exclusionReason}</p>
          )}
          <DataTable<SubclusterMember>
            data={sc.members}
            columns={memberColumns}
            getRowId={(m) => String(m.id)}
            config={PLAIN_TABLE_CONFIG}
          />
        </div>
      );
    },
    [memberColumns]
  );

  const execColumns = useMemo(
    () =>
      executionColumns({
        appliedHeader: subclusterStatusLabel('combined', 'entities'),
        bulkType: 'entities',
        reverseBlocked: reversalRunning,
        onReverse: setRunToReverse,
      }),
    [reversalRunning]
  );

  return (
    <div className={styles.page}>
      <PageHeader
        breadcrumbs={[]}
        title={`Dedupe Run #${runId}`}
        actions={
          <div className={styles.headerActions}>
            <Button
              kind="ghost"
              size="sm"
              onClick={() => history.push('/admin/bulk_dedupe')}
            >
              All runs
            </Button>
            <Button
              kind="danger"
              size="sm"
              onClick={runExecute}
              disabled={
                clustering ||
                mergeableCount === 0 ||
                mergeRunning ||
                executeBusy
              }
            >
              Execute Deduplications ({format0(mergeableCount)})
            </Button>
          </div>
        }
      />

      {error && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={error}
          onClose={() => setError(null)}
        />
      )}

      {clustering && (
        <ProcessProgress label="Generating clusters" status={clusterStatus} />
      )}
      {mergeProc && (
        <ProcessProgress
          label="Executing deduplications"
          status={mergeStatus}
        />
      )}
      {reverseProc && (
        <ProcessProgress
          label="Reversing deduplications"
          status={reverseStatus}
        />
      )}

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Candidates ({format0(total)} sub-clusters)
        </h3>
        <p className={styles.addressLine}>
          Linked by — Address:{' '}
          <strong>{format0(matchTypeCounts.address ?? 0)}</strong> ·
          Alias-confirmed:{' '}
          <strong>{format0(matchTypeCounts.alias ?? 0)}</strong> · Both:{' '}
          <strong>{format0(matchTypeCounts.both ?? 0)}</strong>
        </p>

        {failures.length > 0 && (
          <div className={styles.failuresPanel}>
            <h4 className={styles.failuresTitle}>
              Failed deduplications ({format0(failuresTotal)}
              {failuresTotal > failures.length
                ? ` — showing first ${format0(failures.length)}`
                : ''}
              )
            </h4>
            <DataTable<Subcluster>
              data={failures}
              columns={failureColumns}
              getRowId={(row) => String(row.id)}
              config={PLAIN_TABLE_CONFIG}
            />
          </div>
        )}

        <DataTable<Subcluster>
          data={subclusters}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={subsLoading}
          config={{
            sorting: true,
            globalSearch: false,
            columnFilters: false,
            columnVisibility: false,
            columnReordering: false,
            columnPinning: false,
            export: false,
          }}
          scroll={{
            mode: 'paginated',
            serverDriven: true,
            totalCount: total,
          }}
          initialTableState={{ pageSize: DEFAULT_PAGE_SIZE }}
          onPaginationChange={(s: TableState) =>
            loadSubclusters(s.page, s.pageSize, pageRef.current.sort)
          }
          onSortChange={(s: TableState) =>
            loadSubclusters(1, pageRef.current.pageSize, s.sorting[0] ?? null)
          }
          renderRowSubComponent={renderMembers}
        />
      </section>

      {executions.length > 0 && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Executions</h3>
          <DataTable<Run>
            data={executions}
            columns={execColumns}
            getRowId={(row) => String(row.id)}
            config={PLAIN_TABLE_CONFIG}
          />
        </section>
      )}

      <ReverseConfirmModal
        open={!!runToReverse}
        heading="Reverse deduplication"
        confirmText="Reverse deduplication"
        subject="execution"
        onClose={() => setRunToReverse(null)}
        onConfirm={confirmReverse}
      >
        This will restore the {format0(runToReverse?.combined ?? 0)}{' '}
        deduplicated entit
        {(runToReverse?.combined ?? 0) === 1 ? 'y' : 'ies'} from execution #
        {runToReverse?.id} and revert every merge in it — surviving records will
        give back the document, address, and party associations they absorbed.
      </ReverseConfirmModal>
    </div>
  );
};

// One clustering run, URL-addressable (the run id is the route param), so the
// review survives a refresh and is reachable from the hub at any point in its
// lifecycle. The run's bulk type picks the review surface.
export const BulkDedupeRunPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation<{ bulkType?: BulkType } | undefined>();
  const { bulkType, error, retry } = useRunBulkType(
    Number(id),
    location.state?.bulkType ?? null
  );
  if (error)
    return (
      <div className={styles.page}>
        <ActionableNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Couldn’t load this run"
          subtitle={error}
          actionButtonLabel="Retry"
          onActionButtonClick={retry}
        />
      </div>
    );
  if (!bulkType) return <Loading withOverlay={false} />;
  return bulkType === 'addresses' ? <AddressRunPage /> : <EntityRunPage />;
};
